import {
  AccessScope,
  localeSource,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyFacet,
  TaxonomyTermOrigin,
  TaxonomyTermPriority,
  TaxonomyTermStatus,
  TaxonomyTermSuggestionSource,
} from '@edgebox/sync-core-data-definitions';
import { ExternalServiceId, InternalId, IRuntimeReference, StaticReference, Uuid } from '@edgebox/data-definition-kit';
import {
  ClientLocaleEntity,
  ClientTaxonomyTermDraft,
  ClientTaxonomyTermEntity,
  MonitoringPromptTermAssociationOperation,
  MonitoringPromptTermAssociationType,
  TaxonomyTermService,
} from '@edgebox/sync-core-rest-client';
import { faArrowLeft, faEllipsis, faSearch, faTags } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal } from '@edgebox/react-components';
import React from 'react';
import { toast } from 'react-toastify';
import { Link, useLocation, useNavigate } from 'react-router';
import { EmbeddedModal } from '../../../EmbeddedModal';
import { suggestionAnswersPath } from './suggestionDeepLink.utils';
import { SyncCoreApiContext } from '../../../../contexts/SyncCoreApiContext';
import { AppParamsContext, IAppParams } from '../../../AppParams';
import { useGovernanceTaxonomyManagementContext } from '../GovernanceTaxonomyManagementContext';
import {
  allowTermCreation,
  carriesBrandIdentity,
  createPath,
  DEFAULT_STATUS_FILTER,
  editPath,
  LISTED_STATUSES,
  parseTaxonomyTermCreatePrefill,
  STATUS_FILTER_SET_OPTIONS,
  statusesForFilter,
  StatusFilter,
  taxonomyFacetValue,
  taxonomyPath,
  termDetailPath,
  termRouteReference,
} from '../GovernanceTaxonomyManagement.utils';
import { useEntityDomains } from './useEntityDomains';
import { DefaultApi } from '@edgebox/sync-core-rest-client/dist/services/api/apis';
import { EngineLogo } from './EngineLogo';
import { GovernanceTaxonomyTermDetail, termOwnFacet } from './GovernanceTaxonomyTermDetail';
import {
  ALIAS_MIN_LENGTH,
  applyTermFormValues,
  GovernanceTaxonomyTermForm,
  isPurposeFacet,
  ITaxonomyTermFormValues,
  TaxonomyTermRecord,
} from './GovernanceTaxonomyTermForm';
import { GovernanceTermPromptsPanel } from './GovernanceTermPromptsPanel';
import { localeDisplayName } from './localeDisplay.utils';
import { applyTermLocale } from './localeTerm.utils';
import { MonitoringPromptPickerModal } from './MonitoringPromptPickerModal';
import { useDismissOnOutsideClick } from './useDismissOnOutsideClick';
import { BrandPresencePageHeader } from './BrandPresencePageHeader';
import { BrandPresenceEmptyState } from './BrandPresenceEmptyState';
import { SkeletonBlock, SkeletonRegion } from './BrandPresenceSkeleton';
import { getBrandPresenceTabClassName } from './BrandPresenceTabStyles';
import { forgetTaxonomyTermIndexFacets } from '../Onboarding/ContentInventory/Components/taxonomyTermIndex';
import {
  BP_CARD_CLASSES,
  BP_PORTAL_CLASS_NAME,
  BP_PRIMARY_BUTTON_CLASSES,
  BP_PRIMARY_BUTTON_SMALL_CLASSES,
  BP_TOOLBAR_BUTTON_SMALL_CLASSES,
} from './brandPresenceClasses';
import {
  allowsStatusTransition,
  availableStatusActions,
  isUnapprovedTerm,
  ITaxonomyTermStatusAction,
  primaryStatusAction,
  secondaryStatusActions,
  statusLabel,
  taxonomyTermStatusStyles,
} from './taxonomyTermStatus.utils';
import {
  associationPromptEligibility,
  associationSection,
  summarizeAssociationResults,
  TERM_PROMPT_ASSOCIATION_SECTIONS,
  termPromptSections,
} from './termPromptAssociations.utils';

/** The term page has two faces: what the term is, and what uses it. */
type TermDetailTab = 'details' | 'prompts';

/** Sort key for surfacing the strongest suggestions first (Gap 2). */
function suggestionFrequency(term: ClientTaxonomyTermEntity): number {
  const stats = term.suggestionStats;
  if (!stats) {
    return -1;
  }
  return stats.unitCount ?? stats.runCount ?? 0;
}

/**
 * What kind of evidence produced this suggestion, or nothing when we cannot say.
 *
 * Returns undefined rather than a generic "Suggested" for an unrecognised source: the card is
 * already amber and the status badge already reads Unapproved, so a chip repeating "Suggested"
 * spends a slot without adding a fact. The two real values name something the reader can act on —
 * an alternative the answer discussed, or an attribute it scored.
 */
function suggestionSourceLabel(source?: TaxonomyTermSuggestionSource): string | undefined {
  if (source === TaxonomyTermSuggestionSource.CoSubject) {
    return 'Co-subject';
  }
  if (source === TaxonomyTermSuggestionSource.Aspect) {
    return 'Attribute';
  }
  return undefined;
}

/**
 * "Seen in 4 answers" — the count in the reader's own vocabulary.
 *
 * The chip used to read "Seen in 7 units · 4 runs". "Unit" is an internal attribution concept and
 * "run" is the scheduler's word; neither appears anywhere else in the product's language. An answer
 * is the thing the reader looks at, so the answer count leads and the mention count moves into the
 * tooltip, where the detail is available without two jargon words competing for the same line.
 */
function suggestionSeenLabel(runCount: number): string {
  return `Seen in ${runCount} answer${runCount === 1 ? '' : 's'}`;
}

/** The tooltip behind the chip: what was counted, in full. */
function suggestionSeenHint(runCount: number, unitCount: number): string {
  const answers = `${runCount} answer${runCount === 1 ? '' : 's'}`;
  const mentions = `${unitCount} mention${unitCount === 1 ? '' : 's'}`;
  return `Named in ${mentions} across ${answers}.`;
}

function formatLastSeen(lastSeenAt?: number): string | undefined {
  if (typeof lastSeenAt !== 'number' || !Number.isFinite(lastSeenAt)) {
    return undefined;
  }
  return new Date(lastSeenAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * The statuses a bulk action may target, derived from the per-term transitions.
 *
 * Reading them off `availableStatusActions` rather than listing them again is what keeps a bulk
 * action from offering a move no single term is allowed to make.
 */
const BULK_STATUS_TARGETS: TaxonomyTermStatus[] = Array.from(
  new Set(Object.values(TaxonomyTermStatus).flatMap((status) => availableStatusActions(status).map((action) => action.nextStatus)))
);

function termWord(count: number): string {
  return count === 1 ? 'term' : 'terms';
}

/**
 * What a bulk status change would do to the current selection.
 *
 * A selection mixes statuses, and a transition legal for one term is meaningless for another. The
 * ones it does not apply to are counted rather than dropped, so the operator is told what was left
 * alone instead of being shown a success that quietly covered a subset.
 */
interface IBulkStatusPlan {
  nextStatus: TaxonomyTermStatus;
  eligible: ClientTaxonomyTermEntity[];
  skipped: number;
}

function planBulkStatusChange(selected: ClientTaxonomyTermEntity[], nextStatus: TaxonomyTermStatus): IBulkStatusPlan {
  const eligible = selected.filter((term) => allowsStatusTransition(term.status as TaxonomyTermStatus, nextStatus));

  return { nextStatus, eligible, skipped: selected.length - eligible.length };
}

/** Why some of the selection was left alone, for the operator to read after the fact. */
function skippedStatusChangeSummary(plan: IBulkStatusPlan): string {
  return `${plan.skipped} ${termWord(plan.skipped)} skipped: ${statusLabel(plan.nextStatus)} is not a change their current status allows.`;
}

/**
 * Elements inside a tag that own their own click.
 *
 * The tag as a whole toggles the term's selection, so a click landing on one of these belongs to
 * that control instead. Recognising them by role covers a control added to the tag later; wiring
 * `stopPropagation` onto each control one at a time does not, and fails silently when it stops.
 */
const INTERACTIVE_TAG_CHILD_SELECTOR =
  'a,button,input,select,textarea,label,summary,[role="button"],[role="link"],[role="menuitem"],[role="checkbox"],[contenteditable="true"]';

const TERM_ID_ATTRIBUTE = 'data-term-id';

/** The tag a DOM event happened inside, if it happened inside one. */
function termCardFromEventTarget(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) {
    return null;
  }

  const card = target.closest(`[${TERM_ID_ATTRIBUTE}]`);
  return card instanceof HTMLElement ? card : null;
}

/** The term a click on a tag should toggle, or nothing when the click belongs to a control in it. */
function termIdForTagClick(target: EventTarget | null): string | undefined {
  const card = termCardFromEventTarget(target);
  if (!card || !(target instanceof Element)) {
    return undefined;
  }

  const interactive = target.closest(INTERACTIVE_TAG_CHILD_SELECTOR);
  if (interactive && card.contains(interactive)) {
    return undefined;
  }

  return card.dataset.termId || undefined;
}

/**
 * The term a URL names, whether it named it by key or by id.
 *
 * Links carry the key wherever a term has one, so that is what a cold load resolves first — by-key
 * needs a status to pick a revision, and the statuses the list itself offers are tried in the order
 * it offers them, Active first, which is what a link almost always resolves to. Either lookup falls
 * back to the other: a term created by a customer user carries no key at all (only a WriteAll
 * caller may set one), and until every term is given one both shapes have to keep working.
 */
async function loadTermByReference(service: TaxonomyTermService, reference: string): Promise<ClientTaxonomyTermEntity | undefined> {
  const byKey = async () => {
    for (const status of LISTED_STATUSES) {
      const found = await service.itemByKey(reference as ExternalServiceId, status, true);
      if (found) {
        return found;
      }
    }
    return undefined;
  };

  // A miss is how the id lookup answers "that was a key", so it is not an error here — an error
  // that matters still surfaces from whichever lookup the reference's own shape put first.
  const byId = async () => {
    try {
      return await service.item(reference as InternalId, true);
    } catch {
      return undefined;
    }
  };

  const [first, second] = looksLikeInternalId(reference) ? [byId, byKey] : [byKey, byId];
  const found = await first();
  if (found) {
    return found;
  }

  return second();
}

/**
 * Whether a reference is an internal id rather than a term key.
 *
 * Ids are 24-character hex strings and keys are dotted slugs, so the shape says which lookup to try
 * first. It only decides the ORDER: a reference that defies the guess is still resolved, one
 * request later.
 */
function looksLikeInternalId(reference: string): boolean {
  return /^[0-9a-f]{24}$/i.test(reference);
}

/** The target's aliases with `name` folded in once, matched case-insensitively. */
function mergeAliasList(existing: string[] | undefined, name: string): string[] {
  const aliases = existing ? [...existing] : [];
  const addition = name.trim();
  const lowered = addition.toLowerCase();
  if (aliases.some((entry) => entry.trim().toLowerCase() === lowered)) {
    return aliases;
  }
  return [...aliases, addition];
}

/**
 * Why this suggestion cannot become an alias of `target`, or nothing when it can.
 *
 * Sync Core stores aliases as a plain string array and runs no cross-tag uniqueness check, and at
 * match time a name a second active tag already claims binds to neither. So a name that collides
 * with another active tag's name or alias is refused here before either write, naming that tag.
 */
function conversionRefusal(name: string, target: ClientTaxonomyTermEntity, activeTerms: ClientTaxonomyTermEntity[]): string | undefined {
  const trimmed = name.trim();
  if (trimmed.length < ALIAS_MIN_LENGTH) {
    return `A name needs at least ${ALIAS_MIN_LENGTH} characters to be matched as an alias.`;
  }

  const lowered = trimmed.toLowerCase();
  if (target.name.trim().toLowerCase() === lowered) {
    return `“${target.name}” already carries this name.`;
  }

  for (const other of activeTerms) {
    if (String(other.id) === String(target.id)) {
      continue;
    }
    if (other.name.trim().toLowerCase() === lowered) {
      return `“${other.name}” already uses this name.`;
    }
    if ((other.aliases ?? []).some((alias) => alias.trim().toLowerCase() === lowered)) {
      return `“${other.name}” already lists this as an alias.`;
    }
  }

  return undefined;
}

/** Tag cards drawn while the list loads: the checkbox, the name and its badges, the description. */
const TAG_SKELETON_CARDS = [0, 1, 2, 3].map((card) => (
  <div key={card} aria-hidden="true" className={`${BP_CARD_CLASSES} flex gap-4 p-4`}>
    <SkeletonBlock className="mt-1 h-4 w-4 rounded" />
    <div className="min-w-0 flex-1 space-y-2">
      <div className="flex items-center gap-2">
        <SkeletonBlock className={`h-4 ${card % 2 ? 'w-32' : 'w-44'}`} />
        <SkeletonBlock className="h-5 w-16 rounded-full" />
      </div>
      <SkeletonBlock className="h-3.5 w-full max-w-lg" />
    </div>
  </div>
));

interface ITermActionsMenuProps {
  term: ClientTaxonomyTermEntity;
  open: boolean;
  onToggle: () => void;
  /** Omitted where the form is already open, so its menu carries no redundant Edit. */
  onEdit?: () => void;
  /**
   * The status moves the menu offers, defaulting to every one the tag's status allows.
   *
   * A screen that promoted one to a button of its own passes the rest, so the promoted move is
   * offered once rather than in two places that would then have to agree with each other.
   */
  statusActions?: ITaxonomyTermStatusAction[];
  onStatusChange: (nextStatus: TaxonomyTermStatus) => void;
  onMakeAlias: () => void;
}

/**
 * The kebab of a single tag: Edit, its legal status moves, and — for a suggestion awaiting review —
 * the action that turns it into an alias. Shared so the list row and the tag's own page offer the
 * same actions, each of them once.
 */
function TermActionsMenu({ term, open, onToggle, onEdit, statusActions, onStatusChange, onMakeAlias }: ITermActionsMenuProps) {
  const status = term.status as TaxonomyTermStatus;
  const moves = statusActions ?? availableStatusActions(status);
  const offersMakeAlias = isUnapprovedTerm(status);
  const { name } = term;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  useDismissOnOutsideClick({ open, containerRef, triggerRef, onDismiss: onToggle });

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        aria-label={`Actions for ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onToggle}
      >
        <FontAwesomeIcon icon={faEllipsis} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 w-48 rounded-xl bg-white p-1 shadow-card-hover ring-1 ring-slate-200/80 motion-safe:animate-bp-slide-down"
        >
          {onEdit ? (
            <button
              type="button"
              role="menuitem"
              className="w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-900"
              onClick={onEdit}
            >
              Edit
            </button>
          ) : null}
          {moves.map((action) => (
            <button
              key={action.nextStatus}
              type="button"
              role="menuitem"
              className="w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-900"
              onClick={() => onStatusChange(action.nextStatus)}
            >
              {action.label}
            </button>
          ))}
          {offersMakeAlias ? (
            <button
              type="button"
              role="menuitem"
              className="w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-900"
              onClick={onMakeAlias}
            >
              Make alias of…
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface IMakeAliasOfModalProps {
  sourceTerm: ClientTaxonomyTermEntity;
  activeTerms: ClientTaxonomyTermEntity[];
  loading: boolean;
  saving: boolean;
  error?: string;
  onHide: () => void;
  onConfirm: (target: ClientTaxonomyTermEntity) => void;
}

/**
 * Pick the active tag a suggestion should become an alias of.
 *
 * The active tags of the whole taxonomy are offered, loaded on open independent of the list's status
 * filter, the suggestion itself excluded. A collision the refusal catches disables the confirm and
 * says which tag already holds the name, so nothing is written that match time would then bind to
 * neither tag.
 */
function MakeAliasOfModal({ sourceTerm, activeTerms, loading, saving, error, onHide, onConfirm }: IMakeAliasOfModalProps) {
  const [search, setSearch] = React.useState('');
  const [selectedId, setSelectedId] = React.useState<string | undefined>(undefined);

  const sourceName = sourceTerm.name;

  const candidates = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    return activeTerms
      .filter((term) => String(term.id) !== String(sourceTerm.id))
      .filter((term) => (needle ? term.name.toLowerCase().includes(needle) : true));
  }, [activeTerms, search, sourceTerm.id]);

  const selectedTarget = React.useMemo(() => activeTerms.find((term) => String(term.id) === selectedId), [activeTerms, selectedId]);

  const refusal = selectedTarget ? conversionRefusal(sourceName, selectedTarget, activeTerms) : undefined;
  const blocked = !selectedTarget || !!refusal || saving;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (blocked || !selectedTarget) {
      return;
    }
    onConfirm(selectedTarget);
  };

  return (
    <EmbeddedModal className={BP_PORTAL_CLASS_NAME} show onHide={onHide} scrollable>
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Make “{sourceName}” an alias</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-slate-600">Pick the active tag this suggestion should become an alias of.</p>

          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search active tags…"
            aria-label="Search active tags"
            className="mt-3 w-full rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2 text-sm transition-all duration-150 focus:border-primary focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary/10"
          />

          <div
            role="radiogroup"
            aria-label="Active tags"
            className="mt-3 max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-xl ring-1 ring-inset ring-slate-200/80"
          >
            {loading ? (
              <p className="px-3 py-4 text-sm text-slate-500">Loading active tags…</p>
            ) : !candidates.length ? (
              <p className="px-3 py-4 text-sm text-slate-500">No active tags match.</p>
            ) : (
              candidates.map((term) => {
                const id = String(term.id);
                return (
                  <label key={id} className="flex cursor-pointer items-start gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                    <input
                      type="radio"
                      name="alias-target"
                      className="mt-1"
                      checked={selectedId === id}
                      onChange={() => setSelectedId(id)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-slate-900">{term.name}</span>
                      {term.aliases?.length ? (
                        <span className="mt-0.5 block text-xs text-slate-500">Also known as: {term.aliases.join(', ')}</span>
                      ) : null}
                    </span>
                  </label>
                );
              })
            )}
          </div>

          {selectedTarget && !refusal ? (
            <p className="mt-3 text-sm text-slate-600">
              Add “{sourceName}” as an alias of {selectedTarget.name} and dismiss this suggestion.
            </p>
          ) : null}
          {refusal ? <p className="mt-3 text-sm text-rose-600">{refusal}</p> : null}
          {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}
        </Modal.Body>
        <Modal.Footer>
          <div className="flex w-full items-center justify-end gap-2">
            <button
              type="button"
              onClick={onHide}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              Cancel
            </button>
            <button type="submit" disabled={blocked} className={BP_PRIMARY_BUTTON_CLASSES}>
              {saving ? 'Adding…' : 'Make alias'}
            </button>
          </div>
        </Modal.Footer>
      </form>
    </EmbeddedModal>
  );
}

export function GovernanceTaxonomyManagementContent() {
  const apiContext = React.useContext(SyncCoreApiContext);
  const appParams = React.useContext(AppParamsContext) as IAppParams | null;
  const api = apiContext?.api;
  const projectUuid = appParams?.pageOptions?.projectUuid as Uuid | undefined;
  const navigate = useNavigate();
  const location = useLocation();

  const { routeState, selectedTaxonomy, loadingTaxonomies } = useGovernanceTaxonomyManagementContext();

  /**
   * What another screen already knew about the term it sent the operator here to create.
   *
   * Read from the URL rather than passed through state, so the create form opens prefilled whether
   * the reader clicked "+ Add as source" or pasted the link a colleague sent them.
   */
  const createPrefill = React.useMemo(() => parseTaxonomyTermCreatePrefill(location.search), [location.search]);

  const [loadingTerms, setLoadingTerms] = React.useState(true);
  const [terms, setTerms] = React.useState<ClientTaxonomyTermEntity[]>([]);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>(DEFAULT_STATUS_FILTER);
  const [openMenu, setOpenMenu] = React.useState<string | undefined>(undefined);
  const [locales, setLocales] = React.useState<ClientLocaleEntity[]>([]);
  const [localesLoading, setLocalesLoading] = React.useState(false);
  const [editTerm, setEditTerm] = React.useState<ClientTaxonomyTermEntity | undefined>(undefined);
  const [editTermLoading, setEditTermLoading] = React.useState(false);
  const [termFormSaving, setTermFormSaving] = React.useState(false);
  const [termFormError, setTermFormError] = React.useState<string | undefined>(undefined);
  const [createLocaleSelectionId, setCreateLocaleSelectionId] = React.useState<string | undefined>(undefined);
  const [isLocaleSelectionStepCompleted, setIsLocaleSelectionStepCompleted] = React.useState(false);
  const [detailTab, setDetailTab] = React.useState<TermDetailTab>('details');
  const [selectedTermIds, setSelectedTermIds] = React.useState<string[]>([]);
  const [bulkAssociationType, setBulkAssociationType] = React.useState<MonitoringPromptTermAssociationType | undefined>(undefined);
  const [bulkPickerOpen, setBulkPickerOpen] = React.useState(false);
  const [bulkSaving, setBulkSaving] = React.useState(false);
  const [bulkError, setBulkError] = React.useState<string | undefined>(undefined);
  const [bulkMessage, setBulkMessage] = React.useState<string | undefined>(undefined);
  /** What a bulk action deliberately left alone — neither a success nor a failure. */
  const [bulkNotice, setBulkNotice] = React.useState<string | undefined>(undefined);
  const [bulkNextStatus, setBulkNextStatus] = React.useState<TaxonomyTermStatus | undefined>(undefined);
  const [bulkStatusPlan, setBulkStatusPlan] = React.useState<IBulkStatusPlan | undefined>(undefined);
  /** The suggestion the operator is converting into an alias; the picker is open while it is set. */
  const [aliasSourceTerm, setAliasSourceTerm] = React.useState<ClientTaxonomyTermEntity | undefined>(undefined);
  const [aliasSaving, setAliasSaving] = React.useState(false);
  const [aliasError, setAliasError] = React.useState<string | undefined>(undefined);
  /**
   * The taxonomy's active tags, loaded when the picker opens.
   *
   * These are what a suggestion may be made an alias of, and what the refusal checks a name against.
   * They are loaded on their own request so the picker holds the whole taxonomy's active tags
   * whatever status the list is filtered to — the list can be showing Unapproved alone and the
   * picker still offers every active tag to alias onto.
   */
  const [aliasTargetTerms, setAliasTargetTerms] = React.useState<ClientTaxonomyTermEntity[]>([]);
  const [aliasTargetsLoading, setAliasTargetsLoading] = React.useState(false);

  const requestIdRef = React.useRef(0);
  const aliasTargetsRequestIdRef = React.useRef(0);

  const taxonomyTermService = React.useMemo(() => {
    if (!api) {
      return undefined;
    }

    return api.service.taxonomyTerms as TaxonomyTermService;
  }, [api]);

  const selectedTaxonomyFacet = taxonomyFacetValue(selectedTaxonomy);
  const isLocaleTaxonomy = selectedTaxonomyFacet === TaxonomyFacet.Locale;
  const canAddTerms = allowTermCreation(selectedTaxonomy, selectedTaxonomyFacet);
  /**
   * Whether an existing tag is open — at its own page, or at the edit route the row menu and the
   * page's own Edit action lead to. What they show differs: the page reads, and the edit route
   * holds the form. What they share is the tag itself, and so the loading and the tab strip.
   */
  const onTermPage = routeState.mode === 'detail' || routeState.mode === 'edit';
  /** How the URL named the term: its key where it has one, its id otherwise. */
  const termReference = routeState.termReference;

  const brandIdentityFacet = carriesBrandIdentity(selectedTaxonomyFacet);
  const editTermId = editTerm?.id ? String(editTerm.id) : undefined;
  // Re-reads the open tag's colour from its favicon. Wired only for a brand-identity tag with an id.
  const redetectTermColor = React.useCallback(async () => {
    if (!api || !editTermId) {
      throw new Error('This tag cannot re-detect a colour.');
    }
    // The generated API instance sits under the provider's raw services, off the typed surface;
    // this is the one boundary where the app reaches a controller method by that route.
    return (api as unknown as { services: { api: DefaultApi } }).services.api.taxonomyTermControllerDetectColor({ id: editTermId });
  }, [api, editTermId]);
  // The inferred-domain suggestions, project-wide since the form has no purpose in scope. Fetched
  // only while a brand-identity tag is open; a refusal or an abstention simply offers nothing.
  // The tag form has no purpose in scope, so it opts into the project-wide inference explicitly.
  const entityDomains = useEntityDomains(brandIdentityFacet && routeState.mode === 'edit' ? api : undefined, undefined, {
    includeProjectWide: true,
  });
  const editTermKey = editTerm?.key ? String(editTerm.key) : undefined;
  const domainSuggestion = editTermKey ? entityDomains.get(editTermKey) : undefined;
  /**
   * The facet the open tag's own page is read under: the tag's, not the list's.
   *
   * The two differ for as long as a page opened by URL is still loading its tag, and for good on a
   * tag reached from anywhere but its own taxonomy's list — the list falls back to the first
   * taxonomy of the project, which decides nothing about the tag on screen.
   */
  const openTermFacet = termOwnFacet(editTerm, selectedTaxonomyFacet);
  /**
   * What the strip above the open tag offers, worked out in one place.
   *
   * Only the page that READS the tag lifts a move onto a button of its own and sends the rest to
   * the kebab: the page that holds the form has a Save beside it, and a bare button that rewrites
   * the tag from there is one click away from discarding what was typed, so it keeps every move in
   * the menu. Edit is absent from that page for the same reason — the form is already open on it.
   *
   * Whether the kebab has anything in it is decided here too, and nowhere else: the strip and the
   * row around it both need the answer, and two answers to one question drift apart.
   */
  const readsTerm = routeState.mode === 'detail';
  const openTermStatus = editTerm?.status as TaxonomyTermStatus | undefined;
  const promotedTermAction = readsTerm ? primaryStatusAction(openTermStatus) : undefined;
  const termMenuActions = React.useMemo(
    () => (readsTerm ? secondaryStatusActions(openTermStatus) : availableStatusActions(openTermStatus)),
    [openTermStatus, readsTerm]
  );
  const showsTermMenu = !!editTerm && (termMenuActions.length > 0 || isUnapprovedTerm(openTermStatus));
  const showsTermActions = !!editTerm && (readsTerm || showsTermMenu);
  /**
   * The association sections the open tag can appear in, empty for a facet whose terms no prompt
   * references. Memoized on the facet so the panel's per-section requests fire once.
   */
  const promptSections = React.useMemo(() => termPromptSections(openTermFacet), [openTermFacet]);
  const showsPromptsTab = onTermPage && promptSections.length > 0;
  /**
   * The faces the tag's page offers, and none at all where the facet's terms can appear in no
   * prompt: a strip whose second tab could only ever say "nothing here" is a question the reader
   * has to answer for themselves, and a strip with one tab is not a choice.
   */
  const detailTabs: [TermDetailTab, string][] = showsPromptsTab
    ? [
        ['details', 'Details'],
        ['prompts', 'Prompts'],
      ]
    : [];

  const filteredTerms = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matched = !needle
      ? terms
      : terms.filter((term) => term.name.toLowerCase().includes(needle) || (term.description || '').toLowerCase().includes(needle));

    // Surface the strongest suggestions first by ordering the Unapproved terms by
    // frequency descending, while leaving other statuses in their existing slots.
    const unapprovedSorted = matched
      .filter((term) => isUnapprovedTerm(term.status as TaxonomyTermStatus))
      .slice()
      .sort((left, right) => suggestionFrequency(right) - suggestionFrequency(left));

    if (!unapprovedSorted.length) {
      return matched;
    }

    let index = 0;
    return matched.map((term) => (isUnapprovedTerm(term.status as TaxonomyTermStatus) ? unapprovedSorted[index++] : term));
  }, [search, terms]);

  const loadTerms = React.useCallback(
    async (currentStatusFilter: StatusFilter) => {
      if (!taxonomyTermService || !selectedTaxonomyFacet) {
        return [] as ClientTaxonomyTermEntity[];
      }

      const result = await taxonomyTermService.list(
        { page: 0, itemsPerPage: 500 },
        {
          taxonomyFacet: selectedTaxonomyFacet,
          statuses: statusesForFilter(currentStatusFilter),
        }
      );

      return result.items || [];
    },
    [selectedTaxonomyFacet, taxonomyTermService]
  );

  const refreshTerms = React.useCallback(
    async (nextStatusFilter: StatusFilter) => {
      if (!selectedTaxonomyFacet) {
        setTerms([]);
        setLoadingTerms(false);
        return;
      }

      const requestId = ++requestIdRef.current;
      setLoadingTerms(true);
      // Every write on this screen ends in this re-read. The frame's other screens read the same tags
      // through a shared cache for the session, so it is told to read this vocabulary again as well.
      forgetTaxonomyTermIndexFacets([selectedTaxonomyFacet]);
      const nextTerms = await loadTerms(nextStatusFilter);
      if (requestId !== requestIdRef.current) {
        return;
      }

      setTerms(nextTerms);
      setLoadingTerms(false);
    },
    [loadTerms, selectedTaxonomyFacet]
  );

  React.useEffect(() => {
    setStatusFilter(DEFAULT_STATUS_FILTER);
    void refreshTerms(DEFAULT_STATUS_FILTER);
  }, [refreshTerms, selectedTaxonomyFacet]);

  React.useEffect(() => {
    if (!api || routeState.mode === 'list' || !isLocaleTaxonomy) {
      return;
    }

    let active = true;
    void (async () => {
      try {
        setLocalesLoading(true);
        const result = await api.service.locales.list({ page: 0, itemsPerPage: 500 });
        if (!active) {
          return;
        }

        setLocales((result.items || []).slice().sort((left, right) => localeDisplayName(left).localeCompare(localeDisplayName(right))));
      } finally {
        if (active) {
          setLocalesLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, isLocaleTaxonomy, routeState.mode]);

  React.useEffect(() => {
    if (!onTermPage || !termReference || !taxonomyTermService) {
      setEditTerm(undefined);
      return;
    }

    // The list already holds the term whenever the reader arrived through it, by whichever of the
    // two the URL carried — so the common path costs no request at all.
    const found = terms.find((term) => String(term.key) === termReference || String(term.id) === termReference);
    if (found) {
      setEditTerm(found);
      return;
    }

    let active = true;
    void (async () => {
      try {
        setEditTermLoading(true);
        const loaded = await loadTermByReference(taxonomyTermService, termReference);
        if (!active) {
          return;
        }

        setEditTerm(loaded);
      } catch {
        if (active) {
          setEditTerm(undefined);
          setTermFormError('Unable to load taxonomy term for editing.');
        }
      } finally {
        if (active) {
          setEditTermLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [onTermPage, termReference, taxonomyTermService, terms]);

  React.useEffect(() => {
    if (routeState.mode === 'create' && isLocaleTaxonomy) {
      return;
    }

    setCreateLocaleSelectionId(undefined);
    setIsLocaleSelectionStepCompleted(false);
  }, [isLocaleTaxonomy, routeState.mode]);

  const closeTermForm = React.useCallback(() => {
    setTermFormError(undefined);
    setCreateLocaleSelectionId(undefined);
    setIsLocaleSelectionStepCompleted(false);
    navigate(taxonomyPath(selectedTaxonomyFacet));
  }, [navigate, selectedTaxonomyFacet]);

  const openCreateTermForm = React.useCallback(() => {
    if (!canAddTerms) {
      return;
    }

    setTermFormError(undefined);
    setCreateLocaleSelectionId(undefined);
    setIsLocaleSelectionStepCompleted(false);
    navigate(createPath(selectedTaxonomyFacet));
  }, [canAddTerms, navigate, selectedTaxonomyFacet]);

  const openEditTermForm = React.useCallback(
    (term: ClientTaxonomyTermEntity) => {
      setOpenMenu(undefined);
      setTermFormError(undefined);
      setCreateLocaleSelectionId(undefined);
      setIsLocaleSelectionStepCompleted(false);
      navigate(editPath(selectedTaxonomyFacet, term.id ? String(term.id) : undefined));
    },
    [navigate, selectedTaxonomyFacet]
  );

  const submitCreate = React.useCallback(
    async (values: ITaxonomyTermFormValues) => {
      if (!selectedTaxonomy || !taxonomyTermService || !api) {
        return;
      }

      setTermFormSaving(true);
      setTermFormError(undefined);

      try {
        if (!projectUuid) {
          throw new Error('Project context is missing.');
        }

        const project = await api.service.projects.itemByUuid(projectUuid);

        const draft = new ClientTaxonomyTermDraft();
        draft.accessScope = AccessScope.Project;
        draft.origin = TaxonomyTermOrigin.User;
        draft.taxonomy = new StaticReference(selectedTaxonomy);
        applyTermFormValues(draft as TaxonomyTermRecord, values, selectedTaxonomyFacet);
        // A record created without aliases carries no list at all rather than an empty one.
        draft.aliases = values.aliases?.length ? values.aliases : undefined;

        if (project.customer) {
          draft.customer = project.customer;
        }
        draft.project = new StaticReference(project);

        if (isLocaleTaxonomy) {
          const selectedLocale = locales.find((entry) => String(entry.id) === values.sourceLocaleId);
          if (!selectedLocale) {
            throw new Error('Selected source locale was not found.');
          }

          applyTermLocale(draft, selectedLocale);
        }

        await taxonomyTermService.create(draft);
        setCreateLocaleSelectionId(undefined);
        setIsLocaleSelectionStepCompleted(false);
        navigate(taxonomyPath(selectedTaxonomyFacet));
        await refreshTerms(statusFilter);
      } catch (error) {
        setTermFormError((error as Error)?.message || 'Unable to create taxonomy term.');
      } finally {
        setTermFormSaving(false);
      }
    },
    [
      api,
      isLocaleTaxonomy,
      locales,
      navigate,
      projectUuid,
      refreshTerms,
      selectedTaxonomy,
      selectedTaxonomyFacet,
      statusFilter,
      taxonomyTermService,
      setCreateLocaleSelectionId,
      setIsLocaleSelectionStepCompleted,
    ]
  );

  const shouldShowLocaleSelectionStep = routeState.mode === 'create' && isLocaleTaxonomy && !isLocaleSelectionStepCompleted;

  const submitEdit = React.useCallback(
    async (values: ITaxonomyTermFormValues) => {
      if (!editTerm || !taxonomyTermService || !selectedTaxonomy) {
        return;
      }

      setTermFormSaving(true);
      setTermFormError(undefined);

      try {
        const updated = new ClientTaxonomyTermEntity(editTerm);
        applyTermFormValues(updated as TaxonomyTermRecord, values, selectedTaxonomyFacet);
        // The whole record is sent, so an emptied list must travel as [] — a left-off field keeps
        // the saved aliases, and the last one could never be removed.
        updated.aliases = values.aliases || [];

        if (isLocaleTaxonomy) {
          const selectedLocale = locales.find((entry) => String(entry.id) === values.sourceLocaleId);
          if (!selectedLocale) {
            throw new Error('Selected source locale was not found.');
          }

          updated.origin = TaxonomyTermOrigin.System;
          const localeReference = new StaticReference(selectedLocale);
          (updated as ClientTaxonomyTermEntity & { locale?: IRuntimeReference<ClientLocaleEntity> }).locale = localeReference;
          updated.source = localeSource(localeReference);
        }

        await taxonomyTermService.update(updated);
        navigate(taxonomyPath(selectedTaxonomyFacet));
        await refreshTerms(statusFilter);
      } catch (error) {
        setTermFormError((error as Error)?.message || 'Unable to update taxonomy term.');
      } finally {
        setTermFormSaving(false);
      }
    },
    [
      editTerm,
      isLocaleTaxonomy,
      locales,
      navigate,
      refreshTerms,
      selectedTaxonomy,
      selectedTaxonomyFacet,
      statusFilter,
      taxonomyTermService,
    ]
  );

  const onStatusFilterChange = React.useCallback(
    async (nextStatusFilter: StatusFilter) => {
      setStatusFilter(nextStatusFilter);
      await refreshTerms(nextStatusFilter);
    },
    [refreshTerms]
  );

  const updateTermStatus = React.useCallback(
    async (term: ClientTaxonomyTermEntity, nextStatus: TaxonomyTermStatus) => {
      if (!taxonomyTermService) {
        return;
      }

      try {
        setOpenMenu(undefined);
        const updated = new ClientTaxonomyTermEntity(term);
        updated.status = nextStatus;
        await taxonomyTermService.update(updated);
        await refreshTerms(statusFilter);
      } catch (error) {
        setTermFormError((error as Error)?.message || 'Unable to update taxonomy term status.');
      }
    },
    [refreshTerms, statusFilter, taxonomyTermService]
  );

  const openMakeAlias = React.useCallback(
    (term: ClientTaxonomyTermEntity) => {
      setOpenMenu(undefined);
      setAliasError(undefined);
      setAliasSourceTerm(term);

      // The active tags are fetched fresh for the picker, asking only for Active regardless of the
      // list's current filter, so a list scoped to Unapproved still offers every active tag to
      // alias onto. The refusal checks then run against this loaded set, not the filtered list.
      const requestId = ++aliasTargetsRequestIdRef.current;
      setAliasTargetTerms([]);
      setAliasTargetsLoading(true);
      void (async () => {
        try {
          const active = await loadTerms(TaxonomyTermStatus.Active);
          if (requestId !== aliasTargetsRequestIdRef.current) {
            return;
          }
          setAliasTargetTerms(active);
        } catch (error) {
          if (requestId === aliasTargetsRequestIdRef.current) {
            setAliasError((error as Error)?.message || 'Unable to load active tags.');
          }
        } finally {
          if (requestId === aliasTargetsRequestIdRef.current) {
            setAliasTargetsLoading(false);
          }
        }
      })();
    },
    [loadTerms]
  );

  const applyAliasConversion = React.useCallback(
    async (target: ClientTaxonomyTermEntity) => {
      if (!taxonomyTermService || !aliasSourceTerm) {
        return;
      }

      const refusal = conversionRefusal(aliasSourceTerm.name, target, aliasTargetTerms);
      if (refusal) {
        setAliasError(refusal);
        return;
      }

      setAliasSaving(true);
      setAliasError(undefined);

      // The alias is written first and the suggestion archived second, so a failure between the two
      // never loses the name: the alias stands on the target and the suggestion is still there to
      // dismiss on a second attempt.
      try {
        const updatedTarget = new ClientTaxonomyTermEntity(target);
        updatedTarget.aliases = mergeAliasList(target.aliases, aliasSourceTerm.name);
        await taxonomyTermService.update(updatedTarget);
      } catch (error) {
        setAliasError((error as Error)?.message || 'Unable to add the alias.');
        setAliasSaving(false);
        return;
      }

      try {
        const archivedSource = new ClientTaxonomyTermEntity(aliasSourceTerm);
        archivedSource.status = TaxonomyTermStatus.Archived;
        await taxonomyTermService.update(archivedSource);
      } catch (error) {
        setAliasError((error as Error)?.message || 'The alias was added, but dismissing the suggestion failed. Try dismissing it again.');
        setAliasSaving(false);
        // The target now carries the alias, so the list is refreshed to show it even though the
        // suggestion could not be dismissed.
        await refreshTerms(statusFilter);
        return;
      }

      toast(`Alias added to ${target.name}`);
      setAliasSourceTerm(undefined);
      setAliasSaving(false);
      if (onTermPage) {
        navigate(taxonomyPath(selectedTaxonomyFacet));
      }
      await refreshTerms(statusFilter);
    },
    [aliasTargetTerms, aliasSourceTerm, navigate, onTermPage, refreshTerms, selectedTaxonomyFacet, statusFilter, taxonomyTermService]
  );

  // Multi-select is scoped to what is on screen: filtering the list away must not leave an
  // invisible term in the selection that a later bulk call would silently include.
  React.useEffect(() => {
    const visibleIds = new Set(filteredTerms.map((term) => String(term.id)));
    setSelectedTermIds((current) => current.filter((id) => visibleIds.has(id)));
  }, [filteredTerms]);

  const toggleTermSelection = React.useCallback((termId: string) => {
    setSelectedTermIds((current) => (current.includes(termId) ? current.filter((id) => id !== termId) : [...current, termId]));
  }, []);

  /**
   * One handler for the whole list rather than one per tag.
   *
   * The list holds up to 500 terms in a single page, so a closure per row is 500 allocations on
   * every render of a screen whose whole point in this change was to stop paying for rows nobody
   * asked for.
   */
  const onTermListClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const termId = termIdForTagClick(event.target);
      if (termId) {
        toggleTermSelection(termId);
      }
    },
    [toggleTermSelection]
  );

  const onTermCheckboxChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const termId = termCardFromEventTarget(event.currentTarget)?.dataset.termId;
      if (termId) {
        toggleTermSelection(termId);
      }
    },
    [toggleTermSelection]
  );

  React.useEffect(() => {
    setDetailTab('details');
  }, [routeState.mode, termReference]);

  const assignSelectedTermsToPrompts = React.useCallback(
    async (promptIds: InternalId[]) => {
      if (!api || !bulkAssociationType || !selectedTermIds.length) {
        return;
      }

      setBulkSaving(true);
      setBulkError(undefined);
      setBulkNotice(undefined);

      try {
        const results = await api.governance.monitoringPrompts.bulkTerms({
          termIds: selectedTermIds as InternalId[],
          promptIds,
          associationType: bulkAssociationType,
          operation: MonitoringPromptTermAssociationOperation.Add,
        });

        setBulkMessage(summarizeAssociationResults(results, MonitoringPromptTermAssociationOperation.Add));
        setBulkPickerOpen(false);
        setBulkAssociationType(undefined);
        setSelectedTermIds([]);
      } catch (error) {
        setBulkError((error as Error)?.message || 'Unable to assign the selected terms.');
      } finally {
        setBulkSaving(false);
      }
    },
    [api, bulkAssociationType, selectedTermIds]
  );

  const selectedTerms = React.useMemo(() => {
    const selected = new Set(selectedTermIds);
    return filteredTerms.filter((term) => selected.has(String(term.id)));
  }, [filteredTerms, selectedTermIds]);

  const applyBulkStatusPlan = React.useCallback(
    async (plan: IBulkStatusPlan) => {
      if (!taxonomyTermService) {
        return;
      }

      setBulkSaving(true);
      setBulkError(undefined);
      setBulkMessage(undefined);
      setBulkNotice(undefined);

      let updated = 0;
      const failures: string[] = [];

      // The endpoint takes one term at a time, and these run in sequence so a 500-row selection
      // does not arrive as 500 simultaneous writes. A term that fails does not end the batch: the
      // ones after it are still the operator's instruction, and what failed is reported rather
      // than swallowed.
      for (const term of plan.eligible) {
        try {
          const next = new ClientTaxonomyTermEntity(term);
          next.status = plan.nextStatus;
          await taxonomyTermService.update(next);
          updated++;
        } catch (error) {
          failures.push((error as Error)?.message || 'Unknown error.');
        }
      }

      // Every count the operator needs lands in its own channel, so a partial run reads as partial:
      // what changed, what the transition did not apply to, and what failed.
      if (updated) {
        setBulkMessage(`${updated} ${termWord(updated)} set to ${statusLabel(plan.nextStatus)}.`);
      }
      if (plan.skipped) {
        setBulkNotice(skippedStatusChangeSummary(plan));
      }
      if (failures.length) {
        setBulkError(`${failures.length} ${termWord(failures.length)} could not be updated. ${failures[0]}`);
      }

      setBulkStatusPlan(undefined);
      setBulkNextStatus(undefined);
      setSelectedTermIds([]);
      setBulkSaving(false);

      await refreshTerms(statusFilter);
    },
    [refreshTerms, statusFilter, taxonomyTermService]
  );

  /**
   * Work out what the chosen status would do to the selection and ask before doing it.
   *
   * A bulk archive of a 500-row selection is easy to trigger and tedious to undo, so nothing is
   * written until the operator has seen the count and the target status. When the transition suits
   * none of the selection there is nothing to confirm — that is said outright rather than shown as
   * a dialog whose only usable button is Cancel.
   */
  const openBulkStatusConfirm = React.useCallback(() => {
    if (!bulkNextStatus || !selectedTerms.length) {
      return;
    }

    setBulkError(undefined);
    setBulkMessage(undefined);
    setBulkNotice(undefined);

    const plan = planBulkStatusChange(selectedTerms, bulkNextStatus);
    if (!plan.eligible.length) {
      setBulkNotice(
        `None of the ${selectedTerms.length} selected ${termWord(selectedTerms.length)} can be set to ${statusLabel(bulkNextStatus)} from their current status.`
      );
      return;
    }

    setBulkStatusPlan(plan);
  }, [bulkNextStatus, selectedTerms]);

  if (!selectedTaxonomy) {
    return loadingTaxonomies ? (
      <SkeletonRegion label="Loading..." className="flex-1 overflow-hidden bg-slate-50 px-6 py-8">
        <SkeletonBlock className="h-3 w-40" />
        <SkeletonBlock className="mt-3 h-8 w-56 rounded-lg" />
        <SkeletonBlock className="mt-3 mb-8 h-4 w-72" />
        <div className={`${BP_CARD_CLASSES} mb-6 h-14`} />
        <div className="space-y-3">{TAG_SKELETON_CARDS}</div>
      </SkeletonRegion>
    ) : (
      <div className="flex-1 bg-slate-50">
        <BrandPresenceEmptyState icon={faTags} title="No taxonomy found." />
      </div>
    );
  }

  return (
    <main className="relative px-6 py-8">
      {/* The facet itself is seeded and named by Sync Core: this screen curates the tags in it,
            and offers no control over the taxonomy the tags belong to. */}
      <BrandPresencePageHeader
        eyebrow={
          <>
            Brand Presence <span className="text-slate-300">/</span> Taxonomies
          </>
        }
        title={
          <span key={selectedTaxonomy.name} className="inline-block motion-safe:animate-bp-fade-in">
            {selectedTaxonomy.name}
          </span>
        }
        description={
          <>
            Manage terms for{' '}
            {selectedTaxonomyFacet
              ? SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyFacet', selectedTaxonomyFacet as TaxonomyFacet)
              : selectedTaxonomy.name}
            .
          </>
        }
        actions={
          canAddTerms ? (
            <button type="button" className={BP_PRIMARY_BUTTON_CLASSES} onClick={openCreateTermForm}>
              + {selectedTaxonomy.name}
            </button>
          ) : null
        }
      />

      {routeState.mode === 'list' ? (
        <>
          <div className="flex items-center justify-between gap-4 rounded-t-2xl border border-b-0 border-slate-200/80 bg-white px-4 py-3 motion-safe:animate-bp-fade-in">
            <div className="relative w-full max-w-md">
              <FontAwesomeIcon
                icon={faSearch}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400"
              />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search terms..."
                className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50/70 pl-9 pr-3 text-sm transition-all duration-150 focus:border-primary focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary/10"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm font-semibold text-slate-500">Status:</span>
              <select
                value={statusFilter}
                onChange={(event) => {
                  void onStatusFilterChange(event.target.value as StatusFilter);
                }}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
              >
                {STATUS_FILTER_SET_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                {LISTED_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyTermStatus', status)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Bulk assignment bar. Present but inert with nothing selected, so the capability is
              discoverable before the operator has ticked anything. */}
          <div className="flex flex-wrap items-center gap-3 rounded-b-2xl border border-slate-200/80 bg-slate-50/60 px-4 py-2.5 shadow-card motion-safe:animate-bp-fade-in">
            <label className="mb-0 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-600">
              <input
                type="checkbox"
                aria-label="Select every term shown"
                checked={!!filteredTerms.length && selectedTermIds.length === filteredTerms.length}
                disabled={!filteredTerms.length}
                onChange={() =>
                  setSelectedTermIds((current) =>
                    current.length === filteredTerms.length ? [] : filteredTerms.map((term) => String(term.id))
                  )
                }
              />
              {selectedTermIds.length ? `${selectedTermIds.length} selected` : 'Select all terms'}
            </label>

            <select
              value={bulkAssociationType ?? ''}
              disabled={!selectedTermIds.length}
              onChange={(event) => setBulkAssociationType((event.target.value || undefined) as MonitoringPromptTermAssociationType)}
              className="h-8 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Association type…</option>
              {TERM_PROMPT_ASSOCIATION_SECTIONS.map((section) => (
                <option key={section.type} value={section.type}>
                  {section.label}
                </option>
              ))}
            </select>

            <button
              type="button"
              disabled={!selectedTermIds.length || !bulkAssociationType}
              onClick={() => {
                setBulkError(undefined);
                setBulkMessage(undefined);
                setBulkNotice(undefined);
                setBulkPickerOpen(true);
              }}
              className={BP_PRIMARY_BUTTON_SMALL_CLASSES}
            >
              Assign to prompts…
            </button>

            {/* A status the selection can be moved to. Every target is offered whatever the
                selection currently holds, and the terms it does not apply to are reported as
                skipped — an emptying menu on a mixed selection reads as broken. */}
            <select
              value={bulkNextStatus ?? ''}
              disabled={!selectedTermIds.length || bulkSaving}
              aria-label="Bulk status change"
              onChange={(event) => setBulkNextStatus((event.target.value || undefined) as TaxonomyTermStatus | undefined)}
              className="h-8 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Change status to…</option>
              {BULK_STATUS_TARGETS.map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status)}
                </option>
              ))}
            </select>

            <button
              type="button"
              disabled={!selectedTermIds.length || !bulkNextStatus || bulkSaving}
              onClick={openBulkStatusConfirm}
              className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
            >
              Apply status…
            </button>

            {bulkMessage ? (
              <span className="text-sm font-semibold text-emerald-700 motion-safe:animate-bp-fade-in">{bulkMessage}</span>
            ) : null}
            {bulkNotice ? <span className="text-sm text-amber-700">{bulkNotice}</span> : null}
            {bulkError ? <span className="text-sm text-rose-600">{bulkError}</span> : null}
          </div>

          {/* Selection is toggled here, once for the whole list, rather than by a handler bound to
              each of up to 500 tags. `termIdForTagClick` decides whether a click was for the tag or
              for a control inside it. */}
          <div className="space-y-3 py-6" onClick={onTermListClick}>
            {loadingTerms ? (
              <SkeletonRegion label="Loading..." className="space-y-3">
                {TAG_SKELETON_CARDS}
              </SkeletonRegion>
            ) : null}
            {!loadingTerms && !filteredTerms.length ? (
              <div className={BP_CARD_CLASSES}>
                <BrandPresenceEmptyState icon={faTags} title="No terms found." />
              </div>
            ) : null}
            {!loadingTerms
              ? filteredTerms.map((term) => {
                  const termStatus = term.status as TaxonomyTermStatus;
                  const presentation = taxonomyTermStatusStyles(termStatus);
                  const priority = term.priority as TaxonomyTermPriority | undefined;
                  const showPriority = !!priority && priority !== TaxonomyTermPriority.Medium;
                  const termMenuKey = `term-${term.id}`;
                  const evidencePath = suggestionAnswersPath(term.suggestionStats?.source, term.key as string | undefined, term.name);

                  return (
                    <div
                      key={term.id}
                      data-term-id={String(term.id)}
                      className={`cursor-pointer rounded-xl border p-4 transition-all duration-200 motion-safe:animate-bp-fade-in ${presentation.card}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        {/* The checkbox stays the control the selection is made with: it is what
                            carries the state, the label and the keyboard path. Clicking the tag is
                            a mouse shortcut layered over it, not a replacement for it. */}
                        <input
                          type="checkbox"
                          className="mt-1.5 h-4 w-4 cursor-pointer accent-primary"
                          aria-label={`Select ${term.name}`}
                          checked={selectedTermIds.includes(String(term.id))}
                          onChange={onTermCheckboxChange}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* The name opens the tag's own page; ticking the row is what selects it.
                                The mark sits inside the link so it opens the same page the name does
                                — it names the same tag, so it is not a dead spot beside it. Nothing
                                at all for a tag with no mark, which is every tag outside the engines
                                this app carries a logo for. */}
                            <h4 className="m-0 text-base font-bold">
                              <Link
                                to={termDetailPath(selectedTaxonomyFacet, termRouteReference(term))}
                                className="text-inherit no-underline transition-colors duration-150 hover:text-primary hover:no-underline"
                              >
                                <EngineLogo termKey={term.key as string | undefined} size="name" className="me-2" />
                                {term.name}
                              </Link>
                            </h4>
                            <span
                              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${presentation.badge}`}
                            >
                              {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyTermStatus', termStatus)}
                            </span>
                            {showPriority ? (
                              <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-sky-200 bg-sky-50 text-primary">
                                {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyTermPriority', priority)}
                              </span>
                            ) : null}
                            {isPurposeFacet(selectedTaxonomyFacet) ? (
                              term.purposeType ? (
                                <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-slate-200 bg-slate-50 text-slate-700">
                                  {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue(
                                    'MonitoringPromptPurposeType',
                                    term.purposeType
                                  )}
                                </span>
                              ) : (
                                // A tag from before purpose types existed. Its tab on AI Visibility is blocked
                                // until it is typed, so the list says so where the operator can act on it.
                                <span
                                  className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-amber-200 bg-amber-50 text-amber-700"
                                  title="Edit this purpose and choose its type. Its tab on AI Visibility stays blocked until then."
                                >
                                  Needs a purpose type
                                </span>
                              )
                            ) : null}
                          </div>
                          <p className="mt-1 mb-0 text-sm text-slate-600">{term.description || 'No description provided.'}</p>
                          {term.aliases?.length ? (
                            <p className="text-xs mt-1 text-slate-500">Also known as: {term.aliases.join(', ')}</p>
                          ) : null}
                          {isUnapprovedTerm(termStatus) && term.suggestionStats ? (
                            <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                              {/* The count is the reason this suggestion exists, so it earns the link:
                                  an operator deciding whether to approve a term wants to read the
                                  answers behind the number, not take it on trust. */}
                              {evidencePath ? (
                                <button
                                  type="button"
                                  className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 font-semibold text-amber-700 transition-colors duration-150 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50"
                                  title={`${suggestionSeenHint(
                                    term.suggestionStats.runCount,
                                    term.suggestionStats.unitCount
                                  )} Open the answers it was seen in.`}
                                  onClick={() => navigate(evidencePath)}
                                >
                                  {suggestionSeenLabel(term.suggestionStats.runCount)}
                                </button>
                              ) : (
                                <span
                                  className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 font-semibold text-amber-700"
                                  title={suggestionSeenHint(term.suggestionStats.runCount, term.suggestionStats.unitCount)}
                                >
                                  {suggestionSeenLabel(term.suggestionStats.runCount)}
                                </span>
                              )}
                              {/* Absent entirely when the source says nothing the reader can act on. */}
                              {suggestionSourceLabel(term.suggestionStats.source) ? (
                                <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-slate-600">
                                  {suggestionSourceLabel(term.suggestionStats.source)}
                                </span>
                              ) : null}
                              {formatLastSeen(term.suggestionStats.lastSeenAt) ? (
                                <span className="text-slate-400">Last seen {formatLastSeen(term.suggestionStats.lastSeenAt)}</span>
                              ) : null}
                            </div>
                          ) : null}
                        </div>

                        <TermActionsMenu
                          term={term}
                          open={openMenu === termMenuKey}
                          onToggle={() => setOpenMenu(openMenu === termMenuKey ? undefined : termMenuKey)}
                          onEdit={() => openEditTermForm(term)}
                          onStatusChange={(nextStatus) => {
                            void updateTermStatus(term, nextStatus);
                          }}
                          onMakeAlias={() => openMakeAlias(term)}
                        />
                      </div>
                    </div>
                  );
                })
              : null}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <button type="button" onClick={closeTermForm} className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to terms
          </button>

          {/* The strip belongs to an existing tag: a term being created has no id to associate
              anything with, and nothing to curate yet. It carries the faces of the tag on the left
              — none where the facet's terms appear in no prompt — and the tag's actions on the
              right. A strip with neither would be a rule drawn across the page for nothing. */}
          {onTermPage && (detailTabs.length > 0 || showsTermActions) ? (
            <div className="flex items-center justify-between gap-2 border-b border-slate-200/80">
              <div className="flex items-center gap-1">
                {detailTabs.map(([tab, label]) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setDetailTab(tab)}
                    className={getBrandPresenceTabClassName(detailTab === tab, 'px-4 py-2.5')}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {/* Every action the tag offers, each of them once: Edit, the move that puts the tag
                  into use where the page reading it has one, and the rest behind the kebab. */}
              {showsTermActions && editTerm ? (
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {readsTerm ? (
                    <Link
                      to={editPath(selectedTaxonomyFacet, termReference)}
                      className="inline-flex h-8 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    >
                      Edit
                    </Link>
                  ) : null}
                  {promotedTermAction ? (
                    <button
                      type="button"
                      onClick={() => {
                        void updateTermStatus(editTerm, promotedTermAction.nextStatus);
                      }}
                      className="inline-flex h-8 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    >
                      {promotedTermAction.label}
                    </button>
                  ) : null}
                  {showsTermMenu ? (
                    <TermActionsMenu
                      term={editTerm}
                      open={openMenu === 'term-page'}
                      onToggle={() => setOpenMenu(openMenu === 'term-page' ? undefined : 'term-page')}
                      statusActions={termMenuActions}
                      onStatusChange={(nextStatus) => {
                        void updateTermStatus(editTerm, nextStatus);
                      }}
                      onMakeAlias={() => openMakeAlias(editTerm)}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {showsPromptsTab && detailTab === 'prompts' ? (
            editTermLoading ? (
              <SkeletonRegion label="Loading…" className={`${BP_CARD_CLASSES} max-w-3xl space-y-4 p-6`}>
                <SkeletonBlock className="h-5 w-48" />
                <SkeletonBlock className="h-10 w-full rounded-lg" />
                <SkeletonBlock className="h-24 w-full rounded-lg" />
              </SkeletonRegion>
            ) : api && editTerm ? (
              <GovernanceTermPromptsPanel api={api} term={editTerm} sections={promptSections} />
            ) : (
              <div className="text-sm text-slate-500">Tag not found.</div>
            )
          ) : routeState.mode === 'detail' ? (
            editTermLoading ? (
              <SkeletonRegion label="Loading…" className={`${BP_CARD_CLASSES} max-w-3xl space-y-4 p-6`}>
                <SkeletonBlock className="h-5 w-48" />
                <SkeletonBlock className="h-10 w-full rounded-lg" />
                <SkeletonBlock className="h-24 w-full rounded-lg" />
              </SkeletonRegion>
            ) : editTerm ? (
              <GovernanceTaxonomyTermDetail
                taxonomy={selectedTaxonomy}
                term={editTerm}
                api={api}
                locales={locales}
                localesLoading={localesLoading}
                error={termFormError}
                onShowPrompts={showsPromptsTab ? () => setDetailTab('prompts') : undefined}
              />
            ) : (
              <div className="text-sm text-slate-500">Tag not found.</div>
            )
          ) : shouldShowLocaleSelectionStep ? (
            <div className={`${BP_CARD_CLASSES} max-w-3xl`}>
              <div className="border-b border-slate-200/70 px-6 py-4">
                <h3 className="m-0 text-lg font-bold text-slate-900">Select Source Locale</h3>
                <p className="mt-1 text-sm text-slate-500">Choose the locale first. The term form will be prefilled and ready to save.</p>
              </div>
              <div className="space-y-5 px-6 py-5">
                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700">Source locale</label>
                  <select
                    value={createLocaleSelectionId || ''}
                    onChange={(event) => {
                      setCreateLocaleSelectionId(event.target.value || undefined);
                      setIsLocaleSelectionStepCompleted(false);
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                  >
                    <option value="">Select locale</option>
                    {locales.map((locale) => (
                      <option key={String(locale.id)} value={String(locale.id)}>
                        {localeDisplayName(locale)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-end gap-3 border-t border-slate-200/70 pt-4">
                  <button
                    type="button"
                    onClick={closeTermForm}
                    className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!createLocaleSelectionId}
                    onClick={() => {
                      if (!createLocaleSelectionId) {
                        return;
                      }
                      setIsLocaleSelectionStepCompleted(true);
                    }}
                    className={BP_PRIMARY_BUTTON_CLASSES}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <GovernanceTaxonomyTermForm
              mode={routeState.mode === 'create' ? 'create' : 'edit'}
              taxonomy={selectedTaxonomy}
              term={onTermPage ? editTerm : undefined}
              locales={locales}
              createPrefill={routeState.mode === 'create' ? createPrefill : undefined}
              initialSourceLocaleId={routeState.mode === 'create' ? createLocaleSelectionId : undefined}
              localeRequired={isLocaleTaxonomy}
              loading={onTermPage ? editTermLoading || (isLocaleTaxonomy && localesLoading) : isLocaleTaxonomy && localesLoading}
              saving={termFormSaving}
              error={termFormError}
              onCancel={closeTermForm}
              onSubmit={routeState.mode === 'create' ? submitCreate : submitEdit}
              onRedetectColor={routeState.mode === 'edit' && brandIdentityFacet && editTermId ? redetectTermColor : undefined}
              domainSuggestion={domainSuggestion}
            />
          )}
        </div>
      )}

      {/* Every selected term comes from the taxonomy on screen — the selection is pruned to the
          visible rows — so the taxonomy's facet is the facet of all of them, and the picker can be
          narrowed to the prompts that facet can actually be associated with. */}
      {api && bulkPickerOpen && bulkAssociationType ? (
        <MonitoringPromptPickerModal
          api={api}
          show
          title={`Assign ${selectedTermIds.length} ${selectedTermIds.length === 1 ? 'term' : 'terms'} · ${associationSection(bulkAssociationType).label}`}
          intro={
            <>
              Every selected term will be added to the {associationSection(bulkAssociationType).label.toLowerCase()} terms of every prompt
              you pick.
              {associationSection(bulkAssociationType).rewritesRunHistory ? ' Runs that already happened are updated to match.' : null}
            </>
          }
          eligibility={associationPromptEligibility(bulkAssociationType, selectedTaxonomyFacet)}
          saving={bulkSaving}
          error={bulkError}
          onHide={() => setBulkPickerOpen(false)}
          onConfirm={(promptIds) => {
            void assignSelectedTermsToPrompts(promptIds);
          }}
        />
      ) : null}

      {bulkStatusPlan ? (
        <EmbeddedModal className={BP_PORTAL_CLASS_NAME} show onHide={() => setBulkStatusPlan(undefined)}>
          <Modal.Header closeButton>
            <Modal.Title>
              Set {bulkStatusPlan.eligible.length} {termWord(bulkStatusPlan.eligible.length)} to {statusLabel(bulkStatusPlan.nextStatus)}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-sm text-slate-600">
              {bulkStatusPlan.eligible.length} {termWord(bulkStatusPlan.eligible.length)} will be set to{' '}
              <span className="font-bold">{statusLabel(bulkStatusPlan.nextStatus)}</span>.
            </p>
            {bulkStatusPlan.skipped ? <p className="mt-2 text-sm text-amber-700">{skippedStatusChangeSummary(bulkStatusPlan)}</p> : null}
          </Modal.Body>
          <Modal.Footer>
            <div className="flex w-full items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setBulkStatusPlan(undefined)}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={bulkSaving}
                onClick={() => {
                  void applyBulkStatusPlan(bulkStatusPlan);
                }}
                className={BP_PRIMARY_BUTTON_CLASSES}
              >
                {bulkSaving ? 'Applying…' : `Set to ${statusLabel(bulkStatusPlan.nextStatus)}`}
              </button>
            </div>
          </Modal.Footer>
        </EmbeddedModal>
      ) : null}

      {aliasSourceTerm ? (
        <MakeAliasOfModal
          sourceTerm={aliasSourceTerm}
          activeTerms={aliasTargetTerms}
          loading={aliasTargetsLoading}
          saving={aliasSaving}
          error={aliasError}
          onHide={() => {
            setAliasSourceTerm(undefined);
            setAliasError(undefined);
          }}
          onConfirm={(target) => {
            void applyAliasConversion(target);
          }}
        />
      ) : null}
    </main>
  );
}
